// ============================================================
//  PageBuilder — React + Craft.js with Dynamic Tab Management
//  Architecture : un <Editor key={activePage}> par page.
//  L'état initial est du JSX (pas de JSON manuel).
//  La sauvegarde utilise query.serialize() natif Craft.js.
// ============================================================
/* eslint-disable react/display-name */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

import { Editor, Frame, Element, useEditor } from '@craftjs/core';
import { useParams } from 'react-router-dom';
import { useHeaderSlot } from '../../contexts/HeaderSlotContext';
import useApiContext from '../../hooks/useApiContext';

import type { BlockType, HeaderState } from '../../types/index';
import { DEFAULT_HEADER } from '../../types/index';
import {
  Titre,
  Texte,
  Image,
  Bouton,
  Separateur,
  Espaceur,
  Section,
  Cta,
  Libre,
  Formulaire,
  Pricing,
  Offre,
  ValidationBlock,
  FormFieldInput,
  FormFieldTextarea,
  FormFieldRadio,
  FormFieldCheckbox,
  FormFieldSelect,
  FormContainer,
  RESOLVER,
} from '../../components/PageEditor3/blocks/UserBlocks';
import { Topbar } from '../../components/PageEditor3/editor/Topbar';
import { RightPanel } from '../../components/PageEditor3/editor/RightPanel';
import { SiteHeader } from '../../components/PageEditor3/header/SiteHeader';
import {
  TabProvider,
  useLegacyPageSupport,
} from '../../components/PageEditor3/providers/TabProvider';
import type { EventPageType } from '../../services/Event/Event.repository';
import { exportAllPagesHTML } from '../../utils/htmlExport';
import '../../styles/globals.css';

// ── Default content per page (JSX — no hand-written JSON) ─
// Craft.js serializes these automatically on first render.
// Export these so they can be added to the resolver

export function DefaultAccueil(): React.ReactElement {
  return (
    <Element is="div" canvas id="accueil_content">
      <Titre text="Bienvenue sur le portail exposant" level="h1" />
      <Texte text="Gérez votre participation, choisissez votre stand et accédez à tous les services de l'événement depuis cet espace dédié." />
      <Bouton label="Voir les offres" url="/offres" style="primary" />
      <Separateur />
    </Element>
  );
}

export function DefaultOffres(): React.ReactElement {
  return (
    <Element is="div" canvas id="offres_content">
      <Titre text="Nos offres d'exposition" level="h1" />
      <Texte text="Découvrez tous les stands et services disponibles pour votre participation à l'événement." />
      <Offre
        title="Stand 9m² — Hall A"
        badge="Populaire"
        description="Emplacement premium en zone centrale. Inclut le mobilier de base, la connexion WiFi et deux badges exposant."
        price="3 600"
        currency="€"
        ctaLabel="Réserver ce stand"
        ctaUrl="#"
        imageEmoji="🏢"
        included={[
          'Mobilier de base',
          'Connexion WiFi',
          '2 badges exposant',
          'Électricité',
        ]}
        availability="available"
      />
      <Offre
        title="Stand 18m² — Hall B"
        badge="Exclusif"
        description="Grand espace premium avec visibilité maximale et services inclus."
        price="6 200"
        currency="€"
        ctaLabel="Réserver ce stand"
        ctaUrl="#"
        imageEmoji="🏛"
        included={[
          'Mobilier premium',
          'WiFi dédié',
          '4 badges',
          'Électricité 3kW',
        ]}
        availability="limited"
      />
      <Separateur />
      <Cta
        title="Des questions ?"
        subtitle="Notre équipe est disponible pour vous accompagner."
        btnLabel="Contacter Business France"
        btnUrl="#"
      />
    </Element>
  );
}

export function DefaultValidation(): React.ReactElement {
  return (
    <Element is="div" canvas id="validation_content">
      <Titre text="Validation de commande" level="h1" />
      <ValidationBlock
        title="Récapitulatif de commande"
        company="Acme Corp."
        lines={[
          { label: 'Stand 9m²', val: '3 600 €' },
          { label: 'Options', val: '400 €' },
        ]}
        total="4 000 €"
        status="pending"
        message="Votre commande est en cours de validation par l'équipe Business France. Vous recevrez une confirmation sous 48h."
        submitLabel="Confirmer la commande"
      />
      <Separateur />
    </Element>
  );
}

export function DefaultDynamicTab(): React.ReactElement {
  return (
    <Element is="div" canvas id="dynamic_tab_content">
      <Titre text="Nouvelle page dynamique" level="h1" />
      <Texte text="Commencez à créer le contenu de cette page en ajoutant des blocs depuis la palette de gauche." />
    </Element>
  );
}

const DEFAULT_CONTENT: Record<string, React.ComponentType> = {
  accueil: DefaultAccueil,
  offres: DefaultOffres,
  validation: DefaultValidation,
  // Dynamic tabs will use a generic default or the first available template
};

// Get default content for any tab
function getDefaultContentForTab(
  tabId: string,
  slug: string
): React.ComponentType {
  // First try exact match by slug
  if (DEFAULT_CONTENT[slug]) {
    return DEFAULT_CONTENT[slug];
  }

  // Then try by ID (backward compatibility)
  if (DEFAULT_CONTENT[tabId]) {
    return DEFAULT_CONTENT[tabId];
  }

  // Return the registered DefaultDynamicTab component
  return DefaultDynamicTab;
}

// ── Notifications ─────────────────────────────────────────
function useNotify(): {
  notes: Array<{ id: number; msg: string; type: 'success' | 'info' }>;
  notify: (msg: string, type?: 'success' | 'info') => void;
} {
  const [notes, setNotes] = useState<
    Array<{ id: number; msg: string; type: 'success' | 'info' }>
  >([]);
  const notify = useCallback(
    (msg: string, type: 'success' | 'info' = 'info') => {
      const id = Date.now();
      setNotes((n) => [...n, { id, msg, type }]);
      setTimeout(() => setNotes((n) => n.filter((x) => x.id !== id)), 2500);
    },
    []
  );
  return { notes, notify };
}

// ── QueryBridge — expose query to parent via callback ─────
// Must live inside <Editor>. Called every render (Craft.js is sync).
const QueryBridge = ({
  onQuery,
}: {
  onQuery: (q: ReturnType<typeof useEditor>['query']) => void;
}): null => {
  const { query } = useEditor();
  onQuery(query);
  return null;
};

// ── Inner editor ──────────────────────────────────────────
interface InnerProps {
  activePage: string;
  device: 'desktop' | 'tablet' | 'mobile';
  unsaved: boolean;
  selectedHeader: boolean;
  header: HeaderState;
  savedJson: string | null;
  onSelectHeader: () => void;
  onDeselectHeader: () => void;
  onSetDevice: (d: 'desktop' | 'tablet' | 'mobile') => void;
  onSwitchPage: (pid: string) => void;
  onUpdateHeader: (p: Partial<HeaderState>) => void;
  onSaveJSON: () => void;
  onLoadJSON: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  isPublished: boolean;
  publicUrl?: string | null;
  onOpenPreview: () => void;
  onQuery: (q: ReturnType<typeof useEditor>['query']) => void;
  notify: (msg: string, type?: 'success' | 'info') => void;
}

function InnerEditor({
  activePage,
  device,
  unsaved,
  selectedHeader,
  header,
  savedJson,
  onSelectHeader,
  onDeselectHeader,
  onSetDevice,
  onSwitchPage,
  onUpdateHeader,
  onSaveJSON,
  onLoadJSON,
  onPublish,
  onUnpublish,
  isPublished,
  publicUrl,
  onOpenPreview,
  onQuery,
  notify,
}: InnerProps): React.ReactElement {
  const { actions, query } = useEditor();
  const { claimSlot, releaseSlot } = useHeaderSlot();
  const [slotEl, setSlotEl] = React.useState<HTMLElement | null>(null);

  // Claim the app-header center slot while the editor is mounted
  useEffect(() => {
    claimSlot();
    const raf = requestAnimationFrame(() => {
      setSlotEl(document.getElementById('app-header-center-slot'));
    });
    return () => {
      cancelAnimationFrame(raf);
      setSlotEl(null);
      releaseSlot();
    };
  }, [claimSlot, releaseSlot]);
  const { tabs, activeTab, switchToPageId, currentPageId } =
    useLegacyPageSupport();
  const deviceWidth = { desktop: 900, tablet: 768, mobile: 390 }[device];

  const currentSlug = activePage;

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const el = document.activeElement as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        // Craft.js 0.2.x: pass empty array cast to string to clear selection
        actions.selectNode([] as unknown as string);
        onDeselectHeader();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        actions.history.undo();
      }
      if (e.key === 'Delete') {
        const state = query.getState();
        const nodeId = state.events.selected
          ? Array.from(state.events.selected)[0]
          : null;
        if (nodeId && query.node(nodeId).isDeletable()) actions.delete(nodeId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [actions, query, onDeselectHeader]);

  // Add block from the + button in palette
  const handleAddBlock = useCallback(
    (type: BlockType) => {
      const map: Record<BlockType, React.ComponentType> = {
        titre: Titre as React.ComponentType,
        texte: Texte as React.ComponentType,
        image: Image as React.ComponentType,
        bouton: Bouton as React.ComponentType,
        separateur: Separateur as React.ComponentType,
        espaceur: Espaceur as React.ComponentType,
        section: Section as React.ComponentType,
        cta: Cta as React.ComponentType,
        libre: Libre as React.ComponentType,
        formulaire: Formulaire as React.ComponentType,
        pricing: Pricing as React.ComponentType,
        offre: Offre as React.ComponentType,
        validation: ValidationBlock as React.ComponentType,
        'form-input': FormFieldInput as React.ComponentType,
        'form-textarea': FormFieldTextarea as React.ComponentType,
        'form-radio': FormFieldRadio as React.ComponentType,
        'form-checkbox': FormFieldCheckbox as React.ComponentType,
        'form-select': FormFieldSelect as React.ComponentType,
        'form-container': FormContainer as React.ComponentType,
      };
      const Comp = map[type];
      if (!Comp) return;
      const nodeTree = query.parseReactElement(<Comp />).toNodeTree();
      actions.addNodeTree(nodeTree, 'ROOT');
      notify('Bloc ajouté', 'success');
    },
    [actions, query, notify]
  );

  const DefaultContent = getDefaultContentForTab(activePage, currentSlug);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100vh',
      }}
    >
      <QueryBridge onQuery={onQuery} />

      {/* Portal Topbar controls into the app header center slot */}
      {slotEl &&
        ReactDOM.createPortal(
          <Topbar
            activePage={activePage}
            device={device}
            unsaved={unsaved}
            onSelectHeader={onSelectHeader}
            onSetDevice={onSetDevice}
            onSave={onSaveJSON}
            onLoad={onLoadJSON}
            onPreview={onOpenPreview}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
            isPublished={isPublished}
            publicUrl={publicUrl}
          />,
          slotEl
        )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Canvas */}
        <div
          style={{
            flex: 1,
            background: 'var(--bg)',
            display: 'flex',
            justifyContent: 'center',
            padding: 24,
            overflowY: 'auto',
          }}
          onClick={() => {
            actions.selectNode([] as unknown as string);
            onDeselectHeader();
          }}
        >
          <div
            style={{
              background: 'white',
              boxShadow: 'var(--shadow-md)',
              borderRadius: 12,
              width: '100%',
              maxWidth: deviceWidth,
              minHeight: 'fit-content',
              transition: 'max-width 0.3s ease',
            }}
          >
            <SiteHeader
              header={header}
              activePage={activePage}
              isSelected={selectedHeader}
              onSelect={onSelectHeader}
              onSwitchPage={onSwitchPage}
            />

            <div
              onClick={(e) => e.stopPropagation()}
              style={{ minHeight: 400, paddingBottom: 40 }}
            >
              {savedJson !== null && savedJson !== undefined ? (
                <Frame data={savedJson} />
              ) : (
                <Frame>
                  <DefaultContent />
                </Frame>
              )}
            </div>
          </div>
        </div>

        {/* Right panel: Blocks + Properties tabs */}
        <RightPanel
          onAddBlock={handleAddBlock}
          selectedHeader={selectedHeader}
          header={header}
          onUpdateHeader={onUpdateHeader}
        />
      </div>
    </div>
  );
}

// ── Root PageBuilder ──────────────────────────────────────
export function PageBuilder({
  type = 'editor_d_offer',
}: {
  type?: EventPageType;
}): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const { eventRepository } = useApiContext();
  const [initialTabs, setInitialTabs] = useState<any[]>([]);
  const [tabsLoaded, setTabsLoaded] = useState(false);
  const [saveCallback, setSaveCallback] = useState<
    ((updatedTabs?: any[]) => void) | null
  >(null);

  // Load tabs from API
  useEffect(() => {
    if (!eventId) {
      setTabsLoaded(true);
      return;
    }

    eventRepository
      .getEventPage(eventId, type)
      .then((response) => {
        let pageData = null;
        if (response?.blocks) {
          pageData = response.blocks;
        } else if (response?.header || response?.pages) {
          pageData = response;
        }

        if (
          pageData?.tabs &&
          Array.isArray(pageData.tabs) &&
          pageData.tabs.length > 0
        ) {
          // Ensure all tabs have default values for missing fields
          const tabsWithDefaults = pageData.tabs.map((tab: any) => ({
            ...tab,
            isDefault: tab.isDefault || false,
            isActive: tab.isActive !== false, // Default to true if undefined
            icon: tab.icon || undefined,
            createdAt: tab.createdAt || new Date().toISOString(),
            updatedAt: tab.updatedAt || new Date().toISOString(),
          }));
          setInitialTabs(tabsWithDefaults);
        } else {
          // Create default tabs if none exist in API
          const defaultTabs = [
            {
              id: 'accueil',
              label: 'Accueil',
              slug: 'accueil',
              order: 0,
              isDefault: true,
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
          setInitialTabs(defaultTabs);
        }
        setTabsLoaded(true);
      })
      .catch((error) => {
        console.warn('Failed to load tabs from API:', error);
        setTabsLoaded(true); // Continue with defaults
      });
  }, [eventId, eventRepository, type]);

  if (!tabsLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <TabProvider
      initialTabs={initialTabs}
      onTabUpdate={() => {
        saveCallback?.();
      }}
    >
      <PageBuilderInner type={type} onSetSaveCallback={setSaveCallback} />
    </TabProvider>
  );
}

function PageBuilderInner({
  onSetSaveCallback,
  type = 'editor_d_offer',
}: {
  type?: EventPageType;
  onSetSaveCallback: (callback: () => void) => void;
}): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const { eventRepository } = useApiContext();
  const {
    tabs,
    activeTab,
    switchToPageId,
    currentPageId,
    getPageLabel,
    setTabs,
    setActiveTab,
    isInitialized,
  } = useLegacyPageSupport();

  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop'
  );
  const [unsaved, setUnsaved] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(false);
  const [header, setHeader] = useState<HeaderState>({ ...DEFAULT_HEADER });
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [eventSlug, setEventSlug] = useState<string | null>(null);
  const [companySlug, setCompanySlug] = useState<string | null>(null);

  // Dynamic state - use tab IDs instead of fixed PageId enum
  const [pageJsons, setPageJsons] = useState<Record<string, string | null>>({});

  const queryRef = useRef<ReturnType<typeof useEditor>['query'] | null>(null);
  const { notes, notify } = useNotify();
  const tabsRef = useRef(tabs);

  // Clean up pageJsons when tabs are deleted
  useEffect(() => {
    const currentTabIds = new Set(tabs.map((tab: any) => tab.id));
    setPageJsons((prev) => {
      const cleaned: Record<string, string | null> = {};
      // Only keep entries for tabs that still exist
      Object.keys(prev).forEach((pageId) => {
        if (currentTabIds.has(pageId)) {
          cleaned[pageId] = prev[pageId];
        }
      });
      return cleaned;
    });
    tabsRef.current = tabs;
  }, [tabs]);

  // Load page data from API on mount and initialize dynamic tabs
  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    eventRepository
      .getEventPage(eventId, type)
      .then((response) => {
        // Handle different response structures
        let pageData = null;

        // Case 1: { blocks: { header, pages, ... } }
        if (response?.blocks) {
          pageData = response.blocks;
        }
        // Case 2: { header, pages, ... } (direct structure)
        else if (response?.header || response?.pages) {
          pageData = response;
        }

        if (pageData) {
          if (pageData.header) {
            setHeader(pageData.header);
          }
          if (pageData.pages) {
            setPageJsons(pageData.pages);
          }
          // Set tabs from API or create default tabs if none exist
          if (pageData.tabs && pageData.tabs.length > 0) {
            // Ensure all tabs have default values for missing fields
            const tabsWithDefaults = pageData.tabs.map((tab: any) => ({
              ...tab,
              isDefault: tab.isDefault || false,
              isActive: tab.isActive !== false, // Default to true if undefined
              icon: tab.icon || undefined,
              createdAt: tab.createdAt || new Date().toISOString(),
              updatedAt: tab.updatedAt || new Date().toISOString(),
            }));
            setTabs(tabsWithDefaults);
          } else {
            // Create default "accueil" tab if no tabs exist
            const defaultTabs = [
              {
                id: 'accueil',
                label: 'Accueil',
                slug: 'accueil',
                order: 0,
                isDefault: true,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
            ];
            setTabs(defaultTabs);
          }
        }
        // Restore published state from the EventPage model
        if (response?.is_published) {
          setIsPublished(true);
        }
      })
      .catch((error: any) => {
        console.error('Error loading page:', error);

        // Handle 401 authentication errors
        if (error?.response?.status === 401 || error?.status === 401) {
          notify('Session expirée. Actualisation de la page...', 'info');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          return;
        }

        notify('Erreur lors du chargement', 'info');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [eventId, eventRepository, type, notify]);

  const saveCurrentPage = useCallback(() => {
    if (queryRef.current) {
      const json = queryRef.current.serialize();
      setPageJsons((prev) => ({ ...prev, [currentPageId]: json }));
      setUnsaved(true);
      return json;
    }
    return pageJsons[currentPageId] ?? null;
  }, [currentPageId, pageJsons]);

  const handleSwitchPage = useCallback(
    (pid: string) => {
      if (pid === currentPageId) return;
      // Save current page state before switching (even if empty)
      if (queryRef.current) {
        const currentJson = queryRef.current.serialize();
        setPageJsons((prev) => ({ ...prev, [currentPageId]: currentJson }));
      }
      switchToPageId(pid);
      setSelectedHeader(false);
    },
    [currentPageId, switchToPageId]
  );

  const updateHeader = useCallback((patch: Partial<HeaderState>) => {
    setHeader((prev) => ({ ...prev, ...patch }));
    setUnsaved(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!eventId) {
      notify('Event ID manquant', 'info');
      return;
    }

    try {
      const currentJson = saveCurrentPage();
      const allJsons = { ...pageJsons, [currentPageId]: currentJson };
      const data = {
        version: '1.0.0',
        header,
        pages: allJsons,
        tabs: tabsRef.current.map((t: any, i: number) => ({
          id: t.id,
          label: t.label,
          slug: t.slug,
          order: i,
          isDefault: t.isDefault || false,
          isActive: t.isActive !== false, // Default to true if undefined
          icon: t.icon,
          createdAt: t.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })),
        savedAt: new Date().toISOString(),
      };

      await eventRepository.addPage(eventId, data, type);
      setUnsaved(false);
      notify('Sauvegarde réussie', 'success');
    } catch (error: any) {
      console.error('Error saving page:', error);

      // Handle 401 authentication errors
      if (error?.response?.status === 401 || error?.status === 401) {
        notify('Session expirée. Actualisation de la page...', 'info');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      notify('Erreur lors de la sauvegarde', 'info');
    }
  }, [
    eventId,
    currentPageId,
    header,
    pageJsons,
    tabs,
    saveCurrentPage,
    notify,
    eventRepository,
  ]);

  // Set save callback for tab operations
  useEffect(() => {
    onSetSaveCallback(() => handleSave);
  }, [handleSave, onSetSaveCallback]);

  // Auto-save when unsaved changes exist (debounced) - but only after initialization
  useEffect(() => {
    if (!unsaved || !eventId || !isInitialized) return;
    const timer = setTimeout(() => {
      handleSave();
    }, 3000); // Auto-save after 3 seconds of inactivity
    return () => clearTimeout(timer);
  }, [unsaved, eventId, handleSave, isInitialized]);

  const handleLoad = useCallback(async () => {
    if (!eventId) {
      notify('Event ID manquant', 'info');
      return;
    }

    try {
      setLoading(true);
      const response = await eventRepository.getEventPage(eventId, type);

      // Handle different response structures
      let pageData = null;

      // Case 1: { blocks: { header, pages, ... } }
      if (response?.blocks) {
        pageData = response.blocks;
      }
      // Case 2: { header, pages, ... } (direct structure)
      else if (response?.header || response?.pages) {
        pageData = response;
      }

      if (pageData) {
        if (pageData.pages) {
          setPageJsons(pageData.pages);
        }
        if (pageData.header) {
          setHeader(pageData.header);
        }
        if (pageData.tabs) {
          setTabs(pageData.tabs);
        }
        switchToPageId('accueil');
        notify('État chargé avec succès', 'success');
      }
    } catch (error: any) {
      console.error('Error loading page:', error);

      // Handle 401 authentication errors
      if (error?.response?.status === 401 || error?.status === 401) {
        notify('Session expirée. Actualisation de la page...', 'info');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      notify('Erreur lors du chargement', 'info');
    } finally {
      setLoading(false);
    }
  }, [eventId, eventRepository, type, notify]);

  // ── Publish / Unpublish ───────────────────────────────────
  const handlePublish = useCallback(async () => {
    if (!eventId) {
      notify('Event ID manquant', 'info');
      return;
    }
    try {
      const currentJson = saveCurrentPage();
      const allJsons = { ...pageJsons, [currentPageId]: currentJson };
      const data = {
        version: '1.0.0',
        header,
        pages: allJsons,
        tabs: (
          tabs as Array<{
            id: string;
            label: string;
            slug: string;
            isDefault?: boolean;
            isActive?: boolean;
            icon?: string;
            createdAt?: string;
            updatedAt?: string;
          }>
        ).map((t, i) => ({
          id: t.id,
          label: t.label,
          slug: t.slug,
          order: i,
          isDefault: t.isDefault ?? false,
          isActive: t.isActive, // Preserve exact value (true, false, or undefined)
          icon: t.icon,
          createdAt: t.createdAt ?? new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })),
        savedAt: new Date().toISOString(),
      };
      await eventRepository.addPage(eventId, data, type);
      await eventRepository.publishEventPage(eventId, { blocks: data }, type);
      setIsPublished(true);
      setUnsaved(false);
      notify('Page publiée avec succès', 'success');
    } catch (error: any) {
      console.error('Publish failed:', error);

      // Handle 401 authentication errors
      if (error?.response?.status === 401 || error?.status === 401) {
        notify('Session expirée. Actualisation de la page...', 'info');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      notify('Erreur lors de la publication', 'info');
    }
  }, [
    eventId,
    eventRepository,
    saveCurrentPage,
    pageJsons,
    currentPageId,
    header,
    tabs,
    notify,
  ]);

  const handleUnpublish = useCallback(async () => {
    if (!eventId) return;
    try {
      await eventRepository.unpublishEventPage(eventId, type);
      setIsPublished(false);
      notify('Page dépubliée', 'success');
    } catch (error: any) {
      console.error('Unpublish failed:', error);

      // Handle 401 authentication errors
      if (error?.response?.status === 401 || error?.status === 401) {
        notify('Session expirée. Actualisation de la page...', 'info');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      notify('Erreur lors de la dépublication', 'info');
    }
  }, [eventId, eventRepository, type, notify]);

  // Load event metadata: slug from Event, companySlug from URL
  useEffect(() => {
    if (!eventId) return;
    eventRepository
      .getEventById(eventId)
      .then((evt: any) => {
        // Use slug if present, otherwise fall back to eventId so the URL always resolves
        const identifier = evt?.slug ?? evt?.id ?? eventId;
        if (identifier) setEventSlug(String(identifier));
      })
      .catch((err) => console.error('Load event meta failed:', err));
    // Derive companySlug from URL: /{companySlug}/events/{eventId}/...
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0]) setCompanySlug(parts[0]);
  }, [eventId, eventRepository, type]);

  const publicUrl =
    eventSlug && companySlug
      ? `${window.location.origin}/${companySlug}/published/${eventSlug}`
      : null;

  const handlePreview = useCallback(() => {
    const json = saveCurrentPage();
    if (!json) {
      notify('Aucun contenu à prévisualiser', 'info');
      return;
    }
    // Bundle EVERY configured tab in the preview (not just visited ones)
    // so that tab switching swaps between all pages. Unvisited pages get
    // an empty Craft.js stub so the container still exists for switchTab.
    const EMPTY_STUB = '{}';
    const pagesData: Record<string, string> = {};
    const labels: Record<string, string> = {};
    (tabs as Array<{ id: string; label: string }>).forEach((t) => {
      const tabJson =
        t.id === currentPageId ? json : pageJsons[t.id] ?? EMPTY_STUB;
      pagesData[t.id] = tabJson;
      labels[t.id] = t.label;
    });
    setPreviewHtml(
      exportAllPagesHTML(pagesData, header, currentPageId, labels)
    );
    setShowPreview(true);
  }, [currentPageId, header, pageJsons, tabs, saveCurrentPage, notify]);

  return (
    <>
      {/*
        key={currentPageId} remounts <Editor> on page switch.
        This is the recommended pattern for multi-page in Craft.js.
        Only remount when switching pages, not when data loads.
      */}
      {!loading && (
        <Editor
          key={currentPageId}
          resolver={RESOLVER}
          onNodesChange={() => {
            setUnsaved(true);
            // Capture current page state when nodes change.
            // Guard: on page switch, <Editor> remounts and fires an empty
            // nodes-change BEFORE <Frame data={savedJson}> has deserialized.
            // We must not let that transient empty state stomp the saved
            // content already in pageJsons for this page — otherwise the
            // preview (and the editor on re-visit) will show an empty tab.
            if (!queryRef.current) return;
            const json = queryRef.current.serialize();
            const hasContent = (j: string | null | undefined): boolean => {
              if (!j) return false;
              try {
                const parsed = JSON.parse(j) as {
                  ROOT?: { nodes?: unknown[] };
                };
                return (parsed.ROOT?.nodes?.length ?? 0) > 0;
              } catch {
                return false;
              }
            };
            setPageJsons((prev) => {
              if (!hasContent(json) && hasContent(prev[currentPageId])) {
                return prev; // skip transient empty overwrite
              }
              return { ...prev, [currentPageId]: json };
            });
          }}
          indicator={{
            success: '#2563eb',
            error: '#dc2626',
            transition: 'all 0.15s',
            thickness: 2,
          }}
        >
          <InnerEditor
            activePage={currentPageId}
            device={device}
            unsaved={unsaved}
            selectedHeader={selectedHeader}
            header={header}
            savedJson={pageJsons[currentPageId]}
            onSelectHeader={() => setSelectedHeader(true)}
            onDeselectHeader={() => setSelectedHeader(false)}
            onSetDevice={setDevice}
            onSwitchPage={handleSwitchPage}
            onUpdateHeader={updateHeader}
            onSaveJSON={handleSave}
            onLoadJSON={handleLoad}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            isPublished={isPublished}
            publicUrl={publicUrl}
            onOpenPreview={handlePreview}
            onQuery={(q) => {
              queryRef.current = q;
            }}
            notify={notify}
          />
        </Editor>
      )}

      {showPreview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              background: 'var(--text)',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
            }}
          >
            <span
              style={{ color: 'white', fontSize: 13, fontWeight: 600, flex: 1 }}
            >
              👁 Aperçu — {getPageLabel(currentPageId)}
            </span>
            <button
              className="btn btn-ghost"
              style={{ color: 'rgba(255,255,255,0.6)' }}
              onClick={() => setShowPreview(false)}
            >
              ✕ Fermer
            </button>
          </div>
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              padding: 24,
              background: 'rgba(0,0,0,0.6)',
            }}
          >
            <iframe
              title="Aperçu de la page"
              srcDoc={previewHtml}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{
                background: 'white',
                borderRadius: 12,
                width: '100%',
                height: '100%',
                maxWidth: 1280,
                border: 'none',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {notes.map((n) => (
          <div
            key={n.id}
            style={{
              padding: '12px 16px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              boxShadow: 'var(--shadow-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'white',
              border:
                n.type === 'success'
                  ? '1px solid rgba(22,163,74,0.3)'
                  : '1px solid var(--accent-border)',
              color: n.type === 'success' ? 'var(--green)' : 'var(--accent)',
              animation: 'slideUp 0.2s ease',
            }}
          >
            {n.type === 'success' ? '✓' : 'ℹ'} {n.msg}
          </div>
        ))}
      </div>

      {loading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          Chargement...
        </div>
      )}

      <style>{`
        @keyframes slideUp{
          from{opacity:0;transform:translateY(8px)}
          to{opacity:1;transform:translateY(0)}
        }
        
        /* Element highlighting for editor mode only */
        .craft-selected-highlight {
          position: relative !important;
          outline: 2px solid #2563eb !important;
          outline-offset: 2px !important;
          background: rgba(37, 99, 235, 0.05) !important;
          transition: all 0.15s ease !important;
          z-index: 10 !important;
        }
        
        .craft-selected-highlight::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border: 1px dashed rgba(37, 99, 235, 0.4);
          border-radius: 4px;
          pointer-events: none;
          z-index: 11;
        }
        
        .craft-selected-highlight::after {
          content: '✏️ Editing';
          position: absolute;
          top: -30px;
          left: -2px;
          background: #2563eb;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          z-index: 12;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* Hover effect for better UX */
        .craft-selected-highlight:hover {
          outline-color: #1d4ed8 !important;
          background: rgba(29, 78, 216, 0.08) !important;
        }

        /* Make sure highlighting doesn't interfere with text selection */
        .craft-selected-highlight * {
          user-select: text !important;
        }

        /* Ensure highlighting works with different display types */
        .craft-selected-highlight[style*="display: inline"] {
          display: inline-block !important;
        }

        /* Hide highlighting when printing or in preview mode */
        @media print {
          .craft-selected-highlight {
            outline: none !important;
            background: transparent !important;
          }
          .craft-selected-highlight::before,
          .craft-selected-highlight::after {
            display: none !important;
          }
        }

        /* Hide highlighting in iframe contexts (previews) */
        iframe .craft-selected-highlight {
          outline: none !important;
          background: transparent !important;
        }
        iframe .craft-selected-highlight::before,
        iframe .craft-selected-highlight::after {
          display: none !important;
        }
      `}</style>
    </>
  );
}
